import type {
  AccountContact,
  AccountLocation,
  AccountSearchResult,
  Activity,
  ActivityListResponse,
  Attachment,
  BoardResponse,
  BulkPatchCard,
  Campaign,
  CardClusterResponse,
  CardView,
  CatalogResponse,
  ChannelMix,
  ContactAnnotationsResponse,
  ConversionFunnel,
  CreateCampaign,
  CreateExpense,
  CreateExpenseCategory,
  DayOfWeekHeatmap,
  Expense,
  ExpenseCategory,
  Headline,
  ImportExpenses,
  ImportExpensesResult,
  AreaPostcode,
  AreaSummaryResponse,
  AddRecruitCandidate,
  Competitor,
  CompetitorRecruitStatus,
  CompetitorUpsertResult,
  DbSubscription,
  PatchDbSubscription,
  InsightAction,
  PatchInsightAction,
  PatchCompetitor,
  CoverageMapResponse,
  IdleEvaluationResult,
  PostcodeBand,
  BankReconciliation,
  CleanerPayAudit,
  GrowthInsight,
  LeadSourceAttribution,
  LeadSourceRow,
  LongTermSummary,
  SpendTrend,
  TopPayeeRow,
  LsaLead,
  LsaSummary,
  LsaSyncResult,
  MarketingHeadline,
  CreateUnavailability,
  TechCoverageSummary,
  TechSummary,
  TechUnavailability,
  Message,
  MessageListResponse,
  MonthlySpend,
  NotifyPreview,
  NotifyTemplateKey,
  NotifyTemplateMeta,
  OutboundFunnel,
  OutreachLogRow,
  PatchCampaign,
  PatchCard,
  PatchExpense,
  PnlSummary,
  PostNote,
  PostcodeRow,
  QuoteResponse,
  RecordPayment,
  RepeatCustomers,
  RevenueTrendPoint,
  SaveQuote,
  SendMessage,
  JobBreakdown,
  SetOpeningBalance,
  Statement,
  StrategyProgress,
  StripeOnboardingLink,
  StripeStatusView,
  TechNote,
  Technician,
  TechnicianBalance,
} from "@breeze/shared";

const BASE = "/api";

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${txt}`);
  }
  return (await res.json()) as T;
}

export async function fetchBoard(includeArchived = false): Promise<BoardResponse> {
  const qs = includeArchived ? "?archived=1" : "";
  const res = await fetch(`${BASE}/cards${qs}`, { credentials: "include" });
  return handle<BoardResponse>(res);
}

export async function patchCard(
  cardId: number,
  body: PatchCard
): Promise<{ card: CardView }> {
  const res = await fetch(`${BASE}/cards/${cardId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  return handle<{ card: CardView }>(res);
}

export async function fetchMessages(cardId: number): Promise<MessageListResponse> {
  const res = await fetch(`${BASE}/cards/${cardId}/messages`, {
    credentials: "include",
  });
  return handle<MessageListResponse>(res);
}

export async function sendMessage(body: SendMessage): Promise<{ message: Message }> {
  const res = await fetch(`${BASE}/messages/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  return handle<{ message: Message }>(res);
}

export async function uploadAttachment(file: File): Promise<Attachment> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(`${BASE}/messages/upload-attachment`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  const body = await handle<{ attachment: Attachment }>(res);
  return body.attachment;
}

export async function fetchNotifyTemplates(): Promise<NotifyTemplateMeta[]> {
  const res = await fetch(`${BASE}/notify-templates`, { credentials: "include" });
  const body = await handle<{ templates: NotifyTemplateMeta[] }>(res);
  return body.templates;
}

export async function fetchNotifyPreview(
  vid: number,
  key: NotifyTemplateKey
): Promise<NotifyPreview> {
  const res = await fetch(
    `${BASE}/quotes/${vid}/notify-preview?key=${encodeURIComponent(key)}`,
    { credentials: "include" }
  );
  const body = await handle<{ preview: NotifyPreview }>(res);
  return body.preview;
}

export async function fetchActivity(cardId: number): Promise<ActivityListResponse> {
  const res = await fetch(`${BASE}/cards/${cardId}/activity`, {
    credentials: "include",
  });
  return handle<ActivityListResponse>(res);
}

// Notes save intermittently hangs when the kanban API gets into its
// mysterious connection-pool-exhaustion state. Without a client timeout
// the textarea sits on "saving…" forever. Bail at 20s + retry once after
// a 1.5s gap — second attempt usually lands on a healthy worker.
export async function postNote(
  cardId: number,
  body: PostNote
): Promise<{ activity: Activity }> {
  async function once(timeoutMs: number): Promise<{ activity: Activity }> {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), timeoutMs);
    try {
      const res = await fetch(`${BASE}/cards/${cardId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
        signal: ac.signal,
      });
      return await handle<{ activity: Activity }>(res);
    } finally {
      clearTimeout(timer);
    }
  }
  try {
    return await once(20_000);
  } catch (err) {
    const isAbort =
      err instanceof DOMException && err.name === "AbortError";
    if (!isAbort) throw err;
    await new Promise((r) => setTimeout(r, 1500));
    return once(20_000);
  }
}

export async function bulkPatchCards(
  body: BulkPatchCard
): Promise<{ results: Array<{ cardId: number; ok: boolean; error?: string }> }> {
  const res = await fetch(`${BASE}/cards/bulk-patch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  return handle(res);
}

export async function fetchQuote(vid: number): Promise<QuoteResponse> {
  const res = await fetch(`${BASE}/quotes/${vid}`, { credentials: "include" });
  return handle<QuoteResponse>(res);
}

export type RecordVisitPaymentMethod =
  | "cash"
  | "bacs"
  | "cheque_tech"
  | "cheque_customer"
  | "card_paylink";

/** Mirrors the legacy order-form.php Cash / Cheque / BACS Received + Take
 *  Card actions. For card_paylink the response carries the freshly-minted
 *  paylink URL so the UI can open or copy it. When `amount` is provided
 *  (e.g. partial payment), it's used as the B_TRANSLINK row amount;
 *  otherwise the server defaults to the visit's outstanding balance. */
export async function recordVisitPayment(
  vid: number,
  method: RecordVisitPaymentMethod,
  amount?: number
): Promise<{ ok: true; paylinkUrl: string | null }> {
  const res = await fetch(`${BASE}/quotes/${vid}/record-payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(amount !== undefined ? { method, amount } : { method }),
    credentials: "include",
  });
  return handle<{ ok: true; paylinkUrl: string | null }>(res);
}

// --- Per-visit payment ledger (B_TRANSLINK rows) ---

export type VisitPaymentMethod =
  | "cash"
  | "bacs"
  | "cheque"
  | "stripe_card"
  | "stripe_bank_transfer"
  | "stripetransfer"
  | "correction";

export interface VisitPaymentRow {
  id: number;
  vid: number;
  method: VisitPaymentMethod | "other";
  label: string;
  amount: number;
  bucket: string;
  notes: string | null;
}

export async function listVisitPayments(vid: number): Promise<VisitPaymentRow[]> {
  const res = await fetch(`${BASE}/quotes/${vid}/payments`, {
    credentials: "include",
  });
  const data = await handle<{ payments: VisitPaymentRow[] }>(res);
  return data.payments;
}

export async function addVisitPaymentRow(
  vid: number,
  amount: number,
  method: VisitPaymentMethod,
  notes?: string
): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/quotes/${vid}/payments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount, method, notes }),
    credentials: "include",
  });
  return handle<{ ok: true }>(res);
}

export async function deleteVisitPaymentRow(
  vid: number,
  paymentId: number
): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/quotes/${vid}/payments/${paymentId}`, {
    method: "DELETE",
    credentials: "include",
  });
  return handle<{ ok: true }>(res);
}

// --- Payment audit ---

export interface PaymentAuditEntry {
  id: number;
  vid: number;
  ledgerRowId: number | null;
  action: "insert" | "update_bucket" | "delete";
  tableNameOld: string | null;
  tableNameNew: string | null;
  labelOld: string | null;
  labelNew: string | null;
  amountOld: number | null;
  amountNew: number | null;
  assignUserId: number | null;
  notes: string | null;
  actor: string | null;
  source: string | null;
  createdAt: string;
}

export async function listPaymentAudit(vid: number): Promise<PaymentAuditEntry[]> {
  const res = await fetch(`${BASE}/quotes/${vid}/payment-audit`, {
    credentials: "include",
  });
  const data = await handle<{ audit: PaymentAuditEntry[] }>(res);
  return data.audit;
}

// --- Awaiting payments ---

export interface AwaitingPaymentRow {
  vid: number;
  visitNumberText: string | null;
  cardId: number | null;
  aid: number | null;
  accountName: string | null;
  postcode: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  totalCost: number;
  paid: number;
  outstanding: number;
  paymentTypeLabel: string | null;
  paymentStatusLabel: string | null;
  technicianId: number | null;
  technicianName: string | null;
  lastContactedAt: string | null;
  paymentFollowupNote: string | null;
  paymentFollowupNoteUpdatedAt: string | null;
  paymentFollowupNoteActor: string | null;
}

export interface AwaitingPaymentsResponse {
  outstanding: {
    rows: AwaitingPaymentRow[];
    count: number;
    totalOutstanding: number;
  };
  future: {
    rows: AwaitingPaymentRow[];
    count: number;
    totalOutstanding: number;
  };
}

export async function fetchAwaitingPayments(): Promise<AwaitingPaymentsResponse> {
  const res = await fetch(`${BASE}/awaiting-payments`, { credentials: "include" });
  return handle<AwaitingPaymentsResponse>(res);
}

export async function setPaymentFollowupNote(
  vid: number,
  note: string | null,
): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/visits/${vid}/payment-followup-note`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
    credentials: "include",
  });
  return handle<{ ok: true }>(res);
}

// --- Account balance ---

export interface AccountBalanceResponse {
  aid: number;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
  visitCount: number;
  stripeCustomerId: string | null;
  linkedAids: number[];
  linked: {
    totalInvoiced: number;
    totalPaid: number;
    outstanding: number;
    visitCount: number;
  };
}

export async function fetchAccountBalance(
  aid: number,
  mirror = false
): Promise<{ balance: AccountBalanceResponse; mirroredBalance: number | null }> {
  const url = `${BASE}/accounts/${aid}/balance${mirror ? "?mirror=1" : ""}`;
  const res = await fetch(url, { credentials: "include" });
  return handle<{ balance: AccountBalanceResponse; mirroredBalance: number | null }>(res);
}

export interface PaymentTypeCatalogEntry {
  paymentTypeId: number;
  paymentType: string;
  statuses: Array<{ paymentStatusId: number; paymentStatus: string }>;
}

export async function fetchPaymentMe(): Promise<{ email: string | null; isAdmin: boolean }> {
  const res = await fetch(`${BASE}/payments/me`, { credentials: "include" });
  return handle(res);
}

export async function fetchPaymentTypes(): Promise<PaymentTypeCatalogEntry[]> {
  const res = await fetch(`${BASE}/payment-types`, { credentials: "include" });
  const body = await handle<{ types: PaymentTypeCatalogEntry[] }>(res);
  return body.types;
}

/** Admin direct-edit. Pass null on either or both to reset back to Unknown. */
export async function setVisitPayment(
  vid: number,
  paymentTypeId: number | null,
  paymentStatusId: number | null
): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/quotes/${vid}/payment`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paymentTypeId, paymentStatusId }),
    credentials: "include",
  });
  return handle<{ ok: true }>(res);
}

export async function copyVisit(
  vid: number
): Promise<{ newVid: number; newVisitNumberText: string }> {
  const res = await fetch(`${BASE}/visits/${vid}/copy`, {
    method: "POST",
    credentials: "include",
  });
  return handle(res);
}

// ─── New-Quote / Create-Account flow ───────────────────────────────────

export type NewContactBody = {
  firstName: string;
  lastName: string;
  email: string;
  // Legacy single-field input — kept for back-compat with existing call
  // sites. New callers should send the typed phoneMobile/Home/Work/Other.
  phone: string;
  phoneMobile?: string;
  phoneHome?: string;
  phoneWork?: string;
  phoneOther?: string;
};

export type NewLocationBody = {
  address1: string;
  address2: string;
  address3: string;
  city: string;
  postcode: string;
};

export async function searchAccounts(input: {
  name?: string;
  email?: string;
  phone?: string;
  postcode?: string;
}): Promise<AccountSearchResult[]> {
  const qs = new URLSearchParams();
  if (input.name?.trim()) qs.set("name", input.name.trim());
  if (input.email?.trim()) qs.set("email", input.email.trim());
  if (input.phone?.trim()) qs.set("phone", input.phone.trim());
  if (input.postcode?.trim()) qs.set("postcode", input.postcode.trim());
  if ([...qs.keys()].length === 0) return [];
  const res = await fetch(`${BASE}/accounts/search?${qs.toString()}`, {
    credentials: "include",
  });
  const body = await handle<{ results: AccountSearchResult[] }>(res);
  return body.results;
}

// Single-field account search powering the "Link existing customer" flow.
// One term fans out across name / email / phone / postcode server-side.
export async function searchAccountsByQuery(
  q: string
): Promise<AccountSearchResult[]> {
  const term = q.trim();
  if (!term) return [];
  const res = await fetch(`${BASE}/accounts/search?q=${encodeURIComponent(term)}`, {
    credentials: "include",
  });
  const body = await handle<{ results: AccountSearchResult[] }>(res);
  return body.results;
}

// Link an orphan card (aid=0) to an existing account. Re-points the card and
// writes the calling number back so the same number auto-links next time.
export async function linkCardToAccount(
  cardId: number,
  aid: number
): Promise<{ ok: true; aid: number; cid: number; wroteNumber: string | null }> {
  const res = await fetch(`${BASE}/cards/${cardId}/link-account`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ aid }),
    credentials: "include",
  });
  return handle(res);
}

export async function fetchTechniciansForPostcode(
  postcode: string
): Promise<Technician[]> {
  if (!postcode.trim()) return [];
  const res = await fetch(
    `${BASE}/technicians?postcode=${encodeURIComponent(postcode.trim())}`,
    { credentials: "include" }
  );
  const body = await handle<{ technicians: Technician[] }>(res);
  return body.technicians;
}

export async function fetchAccountContacts(aid: number): Promise<AccountContact[]> {
  const res = await fetch(`${BASE}/accounts/${aid}/contacts`, {
    credentials: "include",
  });
  const body = await handle<{ contacts: AccountContact[] }>(res);
  return body.contacts;
}

export async function fetchAccountLocations(aid: number): Promise<AccountLocation[]> {
  const res = await fetch(`${BASE}/accounts/${aid}/locations`, {
    credentials: "include",
  });
  const body = await handle<{ locations: AccountLocation[] }>(res);
  return body.locations;
}

export async function createAccountContact(
  aid: number,
  body: NewContactBody
): Promise<{ cid: number; contactId: string }> {
  const res = await fetch(`${BASE}/accounts/${aid}/contacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ contact: { cid: number; contactId: string } }>(res);
  return data.contact;
}

export async function createAccountLocation(
  aid: number,
  body: NewLocationBody
): Promise<{ lid: number; locationId: string }> {
  const res = await fetch(`${BASE}/accounts/${aid}/locations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ location: { lid: number; locationId: string } }>(res);
  return data.location;
}

export async function patchAccountFlags(
  aid: number,
  flags: { pita?: boolean; dnc?: boolean; dncMarketing?: boolean }
): Promise<void> {
  const res = await fetch(`${BASE}/accounts/${aid}/flags`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(flags),
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

export async function createAccountVisit(
  aid: number,
  body: { cid: number; lid: number }
): Promise<{ newVid: number; newVisitNumberText: string }> {
  const res = await fetch(`${BASE}/accounts/${aid}/visits`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  return handle(res);
}

export async function createNewAccount(
  body: NewContactBody & { cardId?: number }
): Promise<{
  aid: number;
  accountId: string;
  cid: number;
  contactId: string;
  lid: number;
  locationId: string;
}> {
  const res = await fetch(`${BASE}/accounts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{
    account: {
      aid: number;
      accountId: string;
      cid: number;
      contactId: string;
      lid: number;
      locationId: string;
    };
  }>(res);
  return data.account;
}

export async function saveQuote(
  vid: number,
  body: SaveQuote
): Promise<QuoteResponse> {
  const res = await fetch(`${BASE}/quotes/${vid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  return handle<QuoteResponse>(res);
}

export async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await fetch(`${BASE}/catalog`, { credentials: "include" });
  return handle<CatalogResponse>(res);
}

export async function fetchTechNotes(vid: number): Promise<TechNote[]> {
  const res = await fetch(`${BASE}/quotes/${vid}/tech-notes`, {
    credentials: "include",
  });
  const body = await handle<{ notes: TechNote[] }>(res);
  return body.notes;
}

export async function postTechNote(
  vid: number,
  htmlNotes: string
): Promise<TechNote> {
  const res = await fetch(`${BASE}/quotes/${vid}/tech-notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ htmlNotes }),
    credentials: "include",
  });
  const body = await handle<{ note: TechNote }>(res);
  return body.note;
}

export async function fetchCardCluster(
  cardId: number
): Promise<CardClusterResponse> {
  const res = await fetch(`${BASE}/cards/${cardId}/cluster`, {
    credentials: "include",
  });
  return handle<CardClusterResponse>(res);
}

export type AccountClusterResponse = {
  cluster: import("@breeze/shared").CardCluster;
  cardState: { cardId: number; archived: boolean } | null;
};

export async function fetchAccountCluster(
  aid: number
): Promise<AccountClusterResponse> {
  const res = await fetch(`${BASE}/accounts/${aid}/cluster`, {
    credentials: "include",
  });
  return handle<AccountClusterResponse>(res);
}

export async function ensureLeadCardForAccount(
  aid: number
): Promise<{ cardId: number; action: "existing" | "unarchived" | "created" }> {
  const res = await fetch(`${BASE}/accounts/${aid}/ensure-lead-card`, {
    method: "POST",
    credentials: "include",
  });
  return handle<{ cardId: number; action: "existing" | "unarchived" | "created" }>(
    res
  );
}

export async function initiateCall(
  to: string
): Promise<{ callSid: string | null; status: string | null }> {
  const res = await fetch(`${BASE}/voice/click-to-call`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to }),
  });
  return handle<{ callSid: string | null; status: string | null }>(res);
}

export async function fetchContactAnnotations(
  cid: number
): Promise<ContactAnnotationsResponse> {
  const res = await fetch(`${BASE}/contacts/${cid}/ai-annotations`, {
    credentials: "include",
  });
  return handle<ContactAnnotationsResponse>(res);
}

// ---------------------------------------------------------------------------
// Scheduling Service proxy — find slots + inspect decision log
// Backend lives at /scheduling/v1 on tasker01; we hit kanban's proxy at
// /api/scheduling/* so the bearer token never leaves the server.
// ---------------------------------------------------------------------------

export interface ScheduleSlotResult {
  rank: number;
  tech_id: number;
  tech_name: string;
  tech_commission_pct: number | null;
  slot_start: string;
  slot_end: string;
  is_saturday: boolean;
  distance_miles: number | null;
  travel_minutes: number | null;
  travel_origin: "home" | "previous_booking" | "previous_unknown";
  travel_from_postcode: string | null;
  score: number;
  score_breakdown: {
    distance: number;
    preferred_postcode: number;
    workload: number;
    economics_bonus: number;
    preference_match: number;
    soonest_decay: number;
  };
  reason_codes: string[];
  // Customer-facing discount the website would advertise for this slot.
  // 0 when travel falls outside the configured discount tiers. Driven by the
  // breeze-scheduling settings (DISCOUNT_TIERS), so it always matches what
  // /quote on carpetcleaning.co.uk shows for the same date+slot.
  discount_pct: number;
}

export interface FindSlotsResponse {
  request_id: string;
  job_ref: string | null;
  slots: ScheduleSlotResult[];
  rejected_count: number;
}

export interface FindSlotsRequest {
  postcode: string;
  duration_minutes: number;
  estimated_value_gbp: number;
  job_ref?: string;
  days_ahead?: number;
  exclude_tech_ids?: number[];
}

export async function findSlots(req: FindSlotsRequest): Promise<FindSlotsResponse> {
  const res = await fetch(`${BASE}/scheduling/find-slots`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
    credentials: "include",
  });
  return handle<FindSlotsResponse>(res);
}

export interface DecisionEntry {
  id: number;
  tech_id: number;
  tech_name: string | null;
  candidate_date: string;
  candidate_time: string;
  outcome: "rejected" | "scored" | "recommended";
  rejection_code: string | null;
  score: number | null;
  score_breakdown: ScheduleSlotResult["score_breakdown"] | null;
  travel_origin: string | null;
  travel_from_pc: string | null;
  travel_minutes: number | null;
  distance_miles: number | string | null;
  notes: string | null;
  created_at: string;
}

export interface DecisionsResponse {
  request_id: string;
  summary: {
    recommended: number;
    scored: number;
    rejected: number;
    total: number;
  };
  rejection_histogram: Record<string, number>;
  entries: DecisionEntry[];
}

export async function fetchDecisions(requestId: string): Promise<DecisionsResponse> {
  const res = await fetch(`${BASE}/scheduling/decisions/${requestId}`, {
    credentials: "include",
  });
  return handle<DecisionsResponse>(res);
}

// ---------------------------------------------------------------------------
// Dispatch Board 4 — per-tech blocks (visits + free/busy) for a date range.
// Powers Section 3's day grid. Phase 3 layers find-slots results on top
// (the frontend calls findSlots() separately and merges visually).
// ---------------------------------------------------------------------------

export interface DispatchBlock {
  kind: "visit" | "freebusy";
  vid: number | null;
  customer_name: string | null;
  start: string;
  end: string;
  postcode: string | null;
  value_gbp: number | null;
  status: string | null;
  all_day: boolean;
  source: "B_TVISIT" | "B_TAVAILABILITY" | "B_TFREEBUSY";
  travel_from_job: BlockTravelFromJob | null;
}

export interface TechTravelSummary {
  /** The destination postcode the summary describes. Either the customer
   *  postcode (when one was passed to /blocks) or the first visit of the
   *  day. */
  first_visit_postcode: string;
  miles_from_home: number;
  minutes_from_home: number;
}

/** Per-block travel from the customer's search postcode to this block.
 *  Helps the agent eyeball how far the searched job is from each existing
 *  block in the tech's day. Null when no customer_postcode was passed to
 *  /blocks, the block has no postcode, or the pair isn't in the HERE
 *  local cache. */
export interface BlockTravelFromJob {
  miles: number;
  minutes: number;
}

export interface DispatchTech {
  tech_id: number;
  tech_name: string;
  tech_commission_pct: number | null;
  home_postcode: string;
  blocks: DispatchBlock[];
  travel_summary: TechTravelSummary | null;
}

export interface DispatchBoardResponse {
  from_date: string;
  to_date: string;
  techs: DispatchTech[];
}

export async function fetchDispatchBoard(
  fromDate: string,
  days: number = 1,
  customerPostcode?: string
): Promise<DispatchBoardResponse> {
  const qs = new URLSearchParams({ from: fromDate, days: String(days) });
  if (customerPostcode && customerPostcode.trim()) {
    qs.set("customer_postcode", customerPostcode.trim());
  }
  const res = await fetch(`${BASE}/dispatch/board?${qs.toString()}`, {
    credentials: "include",
  });
  return handle<DispatchBoardResponse>(res);
}

// ─── Schedule board ────────────────────────────────────────────────────────

export interface ScheduleAvailabilityBlock {
  id: number;
  technicianId: number;
  start: string; // "YYYY-MM-DDTHH:MM:SS"
  end: string;
  allDay: boolean;
  location: string | null;
}

export async function listScheduleAvailability(
  from: string,
  to: string
): Promise<ScheduleAvailabilityBlock[]> {
  const res = await fetch(
    `${BASE}/schedule/availability?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { credentials: "include" }
  );
  const body = await handle<{ availability: ScheduleAvailabilityBlock[] }>(res);
  return body.availability;
}

export async function createScheduleAvailability(input: {
  technicianId: number;
  date: string;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  location?: string;
}): Promise<ScheduleAvailabilityBlock> {
  const res = await fetch(`${BASE}/schedule/availability`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
    credentials: "include",
  });
  const body = await handle<{ block: ScheduleAvailabilityBlock }>(res);
  return body.block;
}

export async function deleteScheduleAvailability(id: number): Promise<void> {
  const res = await fetch(`${BASE}/schedule/availability/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

export async function resolveVisitCard(vid: number): Promise<number | null> {
  const res = await fetch(`${BASE}/schedule/visit/${vid}/card`, {
    credentials: "include",
  });
  const body = await handle<{ cardId: number | null }>(res);
  return body.cardId;
}

export async function fetchCard(cardId: number): Promise<CardView> {
  const res = await fetch(`${BASE}/cards/${cardId}`, { credentials: "include" });
  const body = await handle<{ card: CardView }>(res);
  return body.card;
}

export interface RescheduleVisitRequest {
  vid: number;
  tech_id: number;
  new_start: string; // "YYYY-MM-DDTHH:MM:SS"
  new_end: string;
  force?: boolean;
}

export interface RescheduleVisitResponse {
  ok: boolean;
  vid: number;
  tech_id: number;
  scheduledate: string;
  starttime: string;
  endtime: string;
}

export async function rescheduleVisit(
  req: RescheduleVisitRequest
): Promise<RescheduleVisitResponse> {
  const res = await fetch(`${BASE}/dispatch/reschedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    // Pass through 409 conflict message so the UI can offer a "force" override.
    throw new Error(`${res.status} ${res.statusText}: ${txt}`);
  }
  return (await res.json()) as RescheduleVisitResponse;
}

// ---------------------------------------------------------------------------
// Cleaner Earnings dashboard
// ---------------------------------------------------------------------------

export async function fetchEarningsMe(): Promise<{
  email: string | null;
  isEarningsAdmin: boolean;
}> {
  const res = await fetch(`${BASE}/earnings/me`, { credentials: "include" });
  return handle(res);
}

export async function fetchEarningsTechnicians(): Promise<TechnicianBalance[]> {
  const res = await fetch(`${BASE}/earnings/technicians`, {
    credentials: "include",
  });
  const body = await handle<{ technicians: TechnicianBalance[] }>(res);
  return body.technicians;
}

// ---------------------------------------------------------------------------
// Stripe Connect — onboarding + status
// ---------------------------------------------------------------------------

export async function fetchStripeStatus(
  technicianId: number
): Promise<StripeStatusView> {
  const res = await fetch(`${BASE}/stripe-connect/technicians/${technicianId}/status`, {
    credentials: "include",
  });
  const data = await handle<{ status: StripeStatusView }>(res);
  return data.status;
}

export async function generateStripeOnboardingLink(
  technicianId: number
): Promise<StripeOnboardingLink> {
  const res = await fetch(`${BASE}/stripe-connect/technicians/${technicianId}/onboard`, {
    method: "POST",
    credentials: "include",
  });
  const data = await handle<{ link: StripeOnboardingLink }>(res);
  return data.link;
}

export async function refreshStripeStatus(
  technicianId: number
): Promise<StripeStatusView> {
  const res = await fetch(`${BASE}/stripe-connect/technicians/${technicianId}/refresh-status`, {
    method: "POST",
    credentials: "include",
  });
  const data = await handle<{ status: StripeStatusView }>(res);
  return data.status;
}

export async function patchEarningsTechnician(
  technicianId: number,
  body: { chequeTreatedAsCash?: boolean; samedayPaymentEnabled?: boolean }
): Promise<TechnicianBalance> {
  const res = await fetch(`${BASE}/earnings/technicians/${technicianId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ technician: TechnicianBalance }>(res);
  return data.technician;
}

export async function fetchJobBreakdown(vid: number): Promise<JobBreakdown> {
  const res = await fetch(`${BASE}/earnings/jobs/${vid}/breakdown`, {
    credentials: "include",
  });
  const data = await handle<{ breakdown: JobBreakdown }>(res);
  return data.breakdown;
}

export async function fetchStatement(
  technicianId: number,
  start: string,
  end: string
): Promise<Statement> {
  const qs = new URLSearchParams({ start, end });
  const res = await fetch(
    `${BASE}/earnings/technicians/${technicianId}/statement?${qs.toString()}`,
    { credentials: "include" }
  );
  const data = await handle<{ statement: Statement }>(res);
  return data.statement;
}

export async function fetchPastStatements(
  technicianId: number
): Promise<Statement[]> {
  const res = await fetch(
    `${BASE}/earnings/technicians/${technicianId}/statements`,
    { credentials: "include" }
  );
  const data = await handle<{ statements: Statement[] }>(res);
  return data.statements;
}

export async function recordEarningsPayment(
  technicianId: number,
  body: RecordPayment
): Promise<Statement> {
  const res = await fetch(
    `${BASE}/earnings/technicians/${technicianId}/payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
    }
  );
  const data = await handle<{ statement: Statement }>(res);
  return data.statement;
}

export async function setEarningsOpeningBalance(
  technicianId: number,
  body: SetOpeningBalance
): Promise<TechnicianBalance> {
  const res = await fetch(
    `${BASE}/earnings/technicians/${technicianId}/opening-balance`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
    }
  );
  const data = await handle<{ technician: TechnicianBalance }>(res);
  return data.technician;
}

// ---------------------------------------------------------------------------
// Business Growth dashboard
// ---------------------------------------------------------------------------

export async function fetchGrowthMe(): Promise<{ email: string | null; isAdmin: boolean }> {
  const res = await fetch(`${BASE}/business-growth/me`, { credentials: "include" });
  return handle(res);
}

export async function fetchHeadline(month: string): Promise<Headline> {
  const res = await fetch(
    `${BASE}/business-growth/headline?month=${encodeURIComponent(month)}`,
    { credentials: "include" }
  );
  const data = await handle<{ headline: Headline }>(res);
  return data.headline;
}

export async function fetchRevenueTrend(months: number): Promise<RevenueTrendPoint[]> {
  const res = await fetch(`${BASE}/business-growth/revenue-trend?months=${months}`, {
    credentials: "include",
  });
  const data = await handle<{ points: RevenueTrendPoint[] }>(res);
  return data.points;
}

export async function fetchConversionFunnel(start: string, end: string): Promise<ConversionFunnel> {
  const qs = new URLSearchParams({ start, end });
  const res = await fetch(`${BASE}/business-growth/conversion-funnel?${qs.toString()}`, {
    credentials: "include",
  });
  const data = await handle<{ funnel: ConversionFunnel }>(res);
  return data.funnel;
}

export async function fetchTopPostcodes(days: number, limit = 20): Promise<PostcodeRow[]> {
  const res = await fetch(
    `${BASE}/business-growth/top-postcodes?days=${days}&limit=${limit}`,
    { credentials: "include" }
  );
  const data = await handle<{ postcodes: PostcodeRow[] }>(res);
  return data.postcodes;
}

export async function fetchLeadSources(days: number): Promise<LeadSourceRow[]> {
  const res = await fetch(`${BASE}/business-growth/lead-sources?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ sources: LeadSourceRow[] }>(res);
  return data.sources;
}

export async function fetchRepeatCustomers(days: number): Promise<RepeatCustomers> {
  const res = await fetch(`${BASE}/business-growth/repeat-customers?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ repeat: RepeatCustomers }>(res);
  return data.repeat;
}

export async function fetchExpenseCategories(includeArchived = false): Promise<ExpenseCategory[]> {
  const qs = includeArchived ? "?includeArchived=1" : "";
  const res = await fetch(`${BASE}/business-growth/categories${qs}`, {
    credentials: "include",
  });
  const data = await handle<{ categories: ExpenseCategory[] }>(res);
  return data.categories;
}

export async function createExpenseCategory(body: CreateExpenseCategory): Promise<ExpenseCategory> {
  const res = await fetch(`${BASE}/business-growth/categories`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ category: ExpenseCategory }>(res);
  return data.category;
}

export async function archiveExpenseCategory(id: number): Promise<void> {
  const res = await fetch(`${BASE}/business-growth/categories/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

export async function restoreExpenseCategory(id: number): Promise<void> {
  const res = await fetch(`${BASE}/business-growth/categories/${id}/restore`, {
    method: "POST",
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

export async function fetchExpenses(filter?: {
  recurrence?: "recurring_only" | "one_off_only" | "all";
  category?: string;
  start?: string;
  end?: string;
}): Promise<Expense[]> {
  const qs = new URLSearchParams();
  if (filter?.recurrence) qs.set("recurrence", filter.recurrence);
  if (filter?.category) qs.set("category", filter.category);
  if (filter?.start) qs.set("start", filter.start);
  if (filter?.end) qs.set("end", filter.end);
  const url = `${BASE}/business-growth/expenses${qs.size > 0 ? "?" + qs.toString() : ""}`;
  const res = await fetch(url, { credentials: "include" });
  const data = await handle<{ expenses: Expense[] }>(res);
  return data.expenses;
}

export async function createExpense(body: CreateExpense): Promise<Expense> {
  const res = await fetch(`${BASE}/business-growth/expenses`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ expense: Expense }>(res);
  return data.expense;
}

export async function patchExpense(id: number, body: PatchExpense): Promise<Expense> {
  const res = await fetch(`${BASE}/business-growth/expenses/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ expense: Expense }>(res);
  return data.expense;
}

export async function deleteExpense(id: number): Promise<void> {
  const res = await fetch(`${BASE}/business-growth/expenses/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

export async function importExpensesBulk(body: ImportExpenses): Promise<ImportExpensesResult> {
  const res = await fetch(`${BASE}/business-growth/expenses/import-bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ result: ImportExpensesResult }>(res);
  return data.result;
}

export async function fetchMonthlySpend(month: string): Promise<MonthlySpend> {
  const res = await fetch(
    `${BASE}/business-growth/monthly-spend?month=${encodeURIComponent(month)}`,
    { credentials: "include" }
  );
  const data = await handle<{ spend: MonthlySpend }>(res);
  return data.spend;
}

export async function fetchPnl(months: number): Promise<PnlSummary> {
  const res = await fetch(`${BASE}/business-growth/pnl?months=${months}`, {
    credentials: "include",
  });
  const data = await handle<{ pnl: PnlSummary }>(res);
  return data.pnl;
}

export async function fetchLongTerm(months: number): Promise<LongTermSummary> {
  const res = await fetch(`${BASE}/business-growth/long-term?months=${months}`, {
    credentials: "include",
  });
  const data = await handle<{ summary: LongTermSummary }>(res);
  return data.summary;
}

export async function fetchStrategyProgress(): Promise<StrategyProgress> {
  const res = await fetch(`${BASE}/business-growth/strategy-progress`, {
    credentials: "include",
  });
  const data = await handle<{ progress: StrategyProgress }>(res);
  return data.progress;
}

export async function fetchTopPayees(days: number, limit: number): Promise<TopPayeeRow[]> {
  const res = await fetch(`${BASE}/business-growth/top-payees?days=${days}&limit=${limit}`, {
    credentials: "include",
  });
  const data = await handle<{ payees: TopPayeeRow[] }>(res);
  return data.payees;
}

export async function fetchSpendTrend(months: number): Promise<SpendTrend> {
  const res = await fetch(`${BASE}/business-growth/spend-trend?months=${months}`, {
    credentials: "include",
  });
  const data = await handle<{ trend: SpendTrend }>(res);
  return data.trend;
}

export async function fetchBankReconciliation(months: number): Promise<BankReconciliation> {
  const res = await fetch(`${BASE}/business-growth/bank-reconciliation?months=${months}`, {
    credentials: "include",
  });
  const data = await handle<{ reconciliation: BankReconciliation }>(res);
  return data.reconciliation;
}

export async function fetchCleanerPayAudit(months: number): Promise<CleanerPayAudit> {
  const res = await fetch(`${BASE}/business-growth/cleaner-pay-audit?months=${months}`, {
    credentials: "include",
  });
  const data = await handle<{ audit: CleanerPayAudit }>(res);
  return data.audit;
}

export async function fetchInsights(limit = 12): Promise<GrowthInsight[]> {
  const res = await fetch(`${BASE}/business-growth/insights?limit=${limit}`, {
    credentials: "include",
  });
  const data = await handle<{ insights: GrowthInsight[] }>(res);
  return data.insights;
}

export async function refreshInsight(): Promise<GrowthInsight> {
  const res = await fetch(`${BASE}/business-growth/insights/refresh`, {
    method: "POST",
    credentials: "include",
  });
  const data = await handle<{ insight: GrowthInsight }>(res);
  return data.insight;
}

export async function fetchLatestActions(): Promise<InsightAction[]> {
  const res = await fetch(`${BASE}/business-growth/insights/latest/actions`, {
    credentials: "include",
  });
  const data = await handle<{ actions: InsightAction[] }>(res);
  return data.actions;
}

// ---- Spend-cuts plan (same machinery, different mode) ----------------------

export async function fetchSpendCuts(limit = 12): Promise<GrowthInsight[]> {
  const res = await fetch(`${BASE}/business-growth/spend-cuts?limit=${limit}`, {
    credentials: "include",
  });
  const data = await handle<{ insights: GrowthInsight[] }>(res);
  return data.insights;
}

export async function refreshSpendCuts(): Promise<GrowthInsight> {
  const res = await fetch(`${BASE}/business-growth/spend-cuts/refresh`, {
    method: "POST",
    credentials: "include",
  });
  const data = await handle<{ insight: GrowthInsight }>(res);
  return data.insight;
}

export async function fetchLatestSpendCutActions(): Promise<InsightAction[]> {
  const res = await fetch(`${BASE}/business-growth/spend-cuts/latest/actions`, {
    credentials: "include",
  });
  const data = await handle<{ actions: InsightAction[] }>(res);
  return data.actions;
}

export async function patchAction(id: number, body: PatchInsightAction): Promise<InsightAction> {
  const res = await fetch(`${BASE}/business-growth/insights/actions/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ action: InsightAction }>(res);
  return data.action;
}

export async function syncAds(monthsBack: number): Promise<{
  customerId: string;
  start: string;
  end: string;
  rowsPulled: number;
  inserted: number;
  updated: number;
}> {
  const res = await fetch(`${BASE}/business-growth/ads/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ monthsBack }),
    credentials: "include",
  });
  const data = await handle<{ result: { customerId: string; start: string; end: string; rowsPulled: number; inserted: number; updated: number } }>(res);
  return data.result;
}

// ---------------------------------------------------------------------------
// Marketing dashboard
// ---------------------------------------------------------------------------

export async function fetchMarketingMe(): Promise<{ email: string | null; isAdmin: boolean }> {
  const res = await fetch(`${BASE}/marketing/me`, { credentials: "include" });
  return handle(res);
}

export async function fetchMarketingHeadline(month: string): Promise<MarketingHeadline> {
  const res = await fetch(
    `${BASE}/marketing/headline?month=${encodeURIComponent(month)}`,
    { credentials: "include" }
  );
  const data = await handle<{ headline: MarketingHeadline }>(res);
  return data.headline;
}

export async function fetchOutboundFunnel(start: string, end: string): Promise<OutboundFunnel> {
  const qs = new URLSearchParams({ start, end });
  const res = await fetch(`${BASE}/marketing/outbound-funnel?${qs.toString()}`, {
    credentials: "include",
  });
  const data = await handle<{ funnel: OutboundFunnel }>(res);
  return data.funnel;
}

export async function fetchChannelMix(days: number): Promise<ChannelMix> {
  const res = await fetch(`${BASE}/marketing/channel-mix?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ mix: ChannelMix }>(res);
  return data.mix;
}

export async function fetchHeatmap(days: number): Promise<DayOfWeekHeatmap> {
  const res = await fetch(`${BASE}/marketing/heatmap?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ heatmap: DayOfWeekHeatmap }>(res);
  return data.heatmap;
}

export async function fetchLeadSourceAttribution(months: number): Promise<LeadSourceAttribution> {
  const res = await fetch(
    `${BASE}/marketing/lead-source-attribution?months=${months}`,
    { credentials: "include" }
  );
  const data = await handle<{ attribution: LeadSourceAttribution }>(res);
  return data.attribution;
}

export async function fetchOutreachLog(filter?: {
  campaignName?: string;
  limit?: number;
}): Promise<OutreachLogRow[]> {
  const qs = new URLSearchParams();
  if (filter?.campaignName) qs.set("campaignName", filter.campaignName);
  if (filter?.limit) qs.set("limit", String(filter.limit));
  const url = `${BASE}/marketing/outreach-log${qs.size > 0 ? "?" + qs.toString() : ""}`;
  const res = await fetch(url, { credentials: "include" });
  const data = await handle<{ rows: OutreachLogRow[] }>(res);
  return data.rows;
}

export async function fetchCampaigns(): Promise<Campaign[]> {
  const res = await fetch(`${BASE}/marketing/campaigns`, { credentials: "include" });
  const data = await handle<{ campaigns: Campaign[] }>(res);
  return data.campaigns;
}

export async function createCampaign(body: CreateCampaign): Promise<Campaign> {
  const res = await fetch(`${BASE}/marketing/campaigns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ campaign: Campaign }>(res);
  return data.campaign;
}

export async function patchCampaign(id: number, body: PatchCampaign): Promise<Campaign> {
  const res = await fetch(`${BASE}/marketing/campaigns/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ campaign: Campaign }>(res);
  return data.campaign;
}

export async function deleteCampaign(id: number): Promise<void> {
  const res = await fetch(`${BASE}/marketing/campaigns/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

// ---- LSA --------------------------------------------------------------------

export async function fetchLsaSummary(days: number): Promise<LsaSummary> {
  const res = await fetch(`${BASE}/marketing/lsa/summary?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ summary: LsaSummary }>(res);
  return data.summary;
}

export async function fetchLsaLeads(limit: number): Promise<LsaLead[]> {
  const res = await fetch(`${BASE}/marketing/lsa/leads?limit=${limit}`, {
    credentials: "include",
  });
  const data = await handle<{ leads: LsaLead[] }>(res);
  return data.leads;
}

export async function syncLsa(days = 30): Promise<LsaSyncResult> {
  const res = await fetch(`${BASE}/marketing/lsa/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ days }),
    credentials: "include",
  });
  const data = await handle<{ result: LsaSyncResult }>(res);
  return data.result;
}

// ---------------------------------------------------------------------------
// Tech Coverage dashboard
// ---------------------------------------------------------------------------

export async function fetchCoverageMe(): Promise<{ email: string | null; isAdmin: boolean }> {
  const res = await fetch(`${BASE}/coverage/me`, { credentials: "include" });
  return handle(res);
}

export async function fetchCoverageSummary(days: number): Promise<TechCoverageSummary> {
  const res = await fetch(`${BASE}/coverage/summary?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ summary: TechCoverageSummary }>(res);
  return data.summary;
}

export async function fetchIdleTechs(days: number): Promise<TechSummary[]> {
  const res = await fetch(`${BASE}/coverage/idle?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ idle: TechSummary[] }>(res);
  return data.idle;
}

export async function evaluateIdle(days: number): Promise<IdleEvaluationResult> {
  const res = await fetch(`${BASE}/coverage/evaluate-idle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ days }),
    credentials: "include",
  });
  const data = await handle<{ result: IdleEvaluationResult }>(res);
  return data.result;
}

export async function fetchCoverageMap(days: number): Promise<CoverageMapResponse> {
  const res = await fetch(`${BASE}/coverage/map?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ map: CoverageMapResponse }>(res);
  return data.map;
}

export async function fetchUnavailability(): Promise<TechUnavailability[]> {
  const res = await fetch(`${BASE}/coverage/unavailability`, { credentials: "include" });
  const data = await handle<{ unavailability: TechUnavailability[] }>(res);
  return data.unavailability;
}

export async function createUnavailability(body: CreateUnavailability): Promise<TechUnavailability> {
  const res = await fetch(`${BASE}/coverage/unavailability`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ unavailability: TechUnavailability }>(res);
  return data.unavailability;
}

export async function deleteUnavailability(id: number): Promise<void> {
  const res = await fetch(`${BASE}/coverage/unavailability/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  await handle<{ ok: true }>(res);
}

// ---------------------------------------------------------------------------
// Area Analysis dashboard
// ---------------------------------------------------------------------------

export async function fetchAreaSummary(days: number): Promise<AreaSummaryResponse> {
  const res = await fetch(`${BASE}/areas/summary?days=${days}`, {
    credentials: "include",
  });
  const data = await handle<{ summary: AreaSummaryResponse }>(res);
  return data.summary;
}

export async function setPostcodeBand(
  outward: string,
  body: { band: PostcodeBand; notes?: string | null }
): Promise<AreaPostcode> {
  const res = await fetch(`${BASE}/areas/postcode/${encodeURIComponent(outward)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ postcode: AreaPostcode }>(res);
  return data.postcode;
}

export async function fetchCompetitorsByOutward(outward: string): Promise<Competitor[]> {
  const res = await fetch(`${BASE}/areas/competitors?outward=${encodeURIComponent(outward)}`, {
    credentials: "include",
  });
  const data = await handle<{ competitors: Competitor[] }>(res);
  return data.competitors;
}

export async function syncPlaces(outward: string, radiusMeters?: number): Promise<CompetitorUpsertResult> {
  const res = await fetch(`${BASE}/areas/competitors/sync-places`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ outward, ...(radiusMeters ? { radiusMeters } : {}) }),
    credentials: "include",
  });
  const data = await handle<{ result: CompetitorUpsertResult }>(res);
  return data.result;
}

export async function patchCompetitor(id: number, body: PatchCompetitor): Promise<Competitor> {
  const res = await fetch(`${BASE}/areas/competitors/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ competitor: Competitor }>(res);
  return data.competitor;
}

export async function fetchRecruitmentCompetitors(status?: CompetitorRecruitStatus): Promise<Competitor[]> {
  const url = `${BASE}/areas/competitors/recruitment${status ? `?status=${encodeURIComponent(status)}` : ""}`;
  const res = await fetch(url, { credentials: "include" });
  const data = await handle<{ competitors: Competitor[] }>(res);
  return data.competitors;
}

// ---------------------------------------------------------------------------
// DB board (Darren's private subscription tracker)
// ---------------------------------------------------------------------------

export async function fetchDbMe(): Promise<{ email: string | null; isOwner: boolean }> {
  const res = await fetch(`${BASE}/db/me`, { credentials: "include" });
  return handle(res);
}

export async function fetchDbSubscriptions(): Promise<DbSubscription[]> {
  const res = await fetch(`${BASE}/db/subscriptions`, { credentials: "include" });
  const data = await handle<{ subscriptions: DbSubscription[] }>(res);
  return data.subscriptions;
}

export async function patchDbSubscription(id: number, body: PatchDbSubscription): Promise<DbSubscription> {
  const res = await fetch(`${BASE}/db/subscriptions/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ subscription: DbSubscription }>(res);
  return data.subscription;
}

// ---------------------------------------------------------------------------
// Recruitment dashboard
// ---------------------------------------------------------------------------

export async function fetchRecruitCandidates(): Promise<Competitor[]> {
  const res = await fetch(`${BASE}/recruitment/candidates`, { credentials: "include" });
  const data = await handle<{ candidates: Competitor[] }>(res);
  return data.candidates;
}

export async function addRecruitCandidate(body: AddRecruitCandidate): Promise<Competitor> {
  const res = await fetch(`${BASE}/recruitment/candidates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ candidate: Competitor }>(res);
  return data.candidate;
}

export async function patchRecruitCandidate(
  id: number,
  body: PatchCompetitor
): Promise<Competitor> {
  const res = await fetch(`${BASE}/recruitment/candidates/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await handle<{ candidate: Competitor }>(res);
  return data.candidate;
}

export async function shortlistFromCompetitor(competitorId: number): Promise<Competitor> {
  const res = await fetch(`${BASE}/recruitment/candidates/from-competitor/${competitorId}`, {
    method: "POST",
    credentials: "include",
  });
  const data = await handle<{ candidate: Competitor }>(res);
  return data.candidate;
}

// Send the approved cleaner_reach_out WhatsApp Content template to a
// recruitment candidate. Creates the tech kanban card on first send
// (idempotent on subsequent sends) so the conversation thread is captured
// in kanban from message 1. Returns the resulting candidate + cardId.
export async function sendRecruitmentWhatsApp(
  competitorId: number
): Promise<{
  ok: true;
  cardId: number;
  cardCreated: boolean;
  msgId: number;
  candidate: Competitor;
}> {
  const res = await fetch(
    `${BASE}/recruitment/candidates/${competitorId}/send-whatsapp`,
    { method: "POST", credentials: "include" }
  );
  return handle(res);
}

// Send the cold-recruitment email via Gmail (info@carpetcleaning.co.uk).
// Same tech-card create-or-attach behaviour as the WhatsApp version.
export async function sendRecruitmentEmail(
  competitorId: number
): Promise<{
  ok: true;
  cardId: number;
  cardCreated: boolean;
  msgId: number;
  candidate: Competitor;
}> {
  const res = await fetch(
    `${BASE}/recruitment/candidates/${competitorId}/send-email`,
    { method: "POST", credentials: "include" }
  );
  return handle(res);
}

// ─ Team admin (kanban-side replacement for useradmin.php) ────────────────────

export interface TeamSummary {
  id: number;
  nickName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mobile: string | null;
  status: string | null;
  roles: string | null;
  stripeConnectStatus: string | null;
  stripeConnectPayoutsEnabled: number;
  insuranceStatus: string | null;
  insuranceExpiry: string | null;
  defaultLocation: string | null;
  lastVisitDate: string | null;
}

export interface TechVehicle {
  id: number;
  reg: string;
  make: string;
  colour: string;
  type: string;
}

export interface TeamDetail extends TeamSummary {
  vehicles: TechVehicle[];
  servicesOther: string | null;
  insuranceDocUrl: string | null;
  insuranceDocFilename: string | null;
  insuranceDocUploadedAt: string | null;
  insuranceVerifiedBy: string | null;
  insuranceVerifiedAt: string | null;
  whatsappNumber: string | null;
  businessType: string | null;
  vatRegistered: string | null;
  businessName: string | null;
  businessAddressL1: string | null;
  businessAddressL2: string | null;
  businessAddressCity: string | null;
  businessAddressCode: string | null;
  insuranceInsurer: string | null;
  insuranceCoverValue: string | null;
  locationsCovered: string | null;
  countyCovered: string | null;
  serviceCodes: string[];
  vehicleReg: string | null;
  vehicleMake: string | null;
  vehicleColour: string | null;
  vehicleType: string | null;
  notifyEmailNewJob: string | null;
  notifyEmailCancelled: string | null;
  notifySmsNewJob: string | null;
  notifySmsCancelled: string | null;
  notifyWhatsappNewJob: string | null;
  notifyWhatsappCancelled: string | null;
  notifySmsQuietHours: string | null;
  stripeConnectAccountId: string | null;
  stripeConnectChargesEnabled: number;
  commissionPlan: string | null;
  markerColour: string | null;
  passwordHashSet: boolean;
  freeBusyIcsUrl: string | null;
  freeBusyLastSync: string | null;
  freeBusyStatus: string | null;
  calendarFeedToken: string | null;
}

export interface TeamAdminPatch {
  status?: string;
  insuranceStatus?: "Required" | "Verifying" | "Approved" | "Disapproved";
  nickName?: string;
  commissionPlan?: string;
  defaultLocation?: string;
  markerColour?: string;
}

export async function fetchTeam(): Promise<{ technicians: TeamSummary[] }> {
  const res = await fetch(`${BASE}/team`, { credentials: "include" });
  return handle(res);
}

export async function fetchTechnicianDetail(
  id: number
): Promise<{ technician: TeamDetail }> {
  const res = await fetch(`${BASE}/team/${id}`, { credentials: "include" });
  return handle(res);
}

export async function patchTechnicianAdmin(
  id: number,
  patch: TeamAdminPatch
): Promise<{ technician: TeamDetail }> {
  const res = await fetch(`${BASE}/team/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return handle(res);
}

// ─── Waiting List (Discount Saver signups) ───────────────────────────────────

export type WaitingListStatus = "waiting" | "notified" | "in_pipeline";

export interface WaitingListSignup {
  id: number;
  firstName: string;
  postcode: string;
  whatsapp: string;
  signedUpAt: string | null;
  notifiedAt: string | null;
  notifiedSlot: string | null;
  status: WaitingListStatus;
  cardId: number | null;
  // Live availability probe for the postcode (null if scheduling service
  // unavailable or row past the probe cap). `slots` = half-day blocks with a
  // tech available in the next 60 days; `discounted` = how many of those are
  // discount-flagged (what actually triggers an auto-notification).
  live: { slots: number; discounted: number } | null;
}

export interface WaitingListResponse {
  signups: WaitingListSignup[];
  counts: { waiting: number; notified: number; inPipeline: number; total: number };
}

export async function fetchWaitingList(): Promise<WaitingListResponse> {
  const res = await fetch(`${BASE}/waiting-list`, { credentials: "include" });
  return handle<WaitingListResponse>(res);
}

export async function createWaitingListLead(
  id: number
): Promise<{ cardId: number; created: boolean }> {
  const res = await fetch(`${BASE}/waiting-list/${id}/create-lead`, {
    method: "POST",
    credentials: "include",
  });
  return handle<{ cardId: number; created: boolean }>(res);
}

export async function dismissWaitingListSignup(id: number): Promise<{ ok: true }> {
  const res = await fetch(`${BASE}/waiting-list/${id}/dismiss`, {
    method: "POST",
    credentials: "include",
  });
  return handle<{ ok: true }>(res);
}
